'use client';

import { useState, useMemo } from 'react';
import { Card, CardBody, CardHeader, Button, Slider, Chip, Divider, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Checkbox, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Input } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ChainType } from '@/constants/chain-specific';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import { useLiquidityManagement } from '@/hooks/liquidity/useLiquidityManagement';
import { formatNumber } from '@/util/balance/formatNumber';
import { TokenLogo } from '@/components/Tokens/TokenLogo';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import { useNativeTokenHandler } from '@/hooks/liquidity/useNativeTokenHandler';
import type { Address } from 'viem';
interface RemoveLiquidityViewProps {
  position: LiquidityPosition;
  chainId?: ChainType;
  onBack: () => void;
  onSuccess?: () => void;
  refetchPosition?: (pairAddress: string, chainId: number, dexSlug: string) => Promise<void>;
}
export function RemoveLiquidityView({
  position,
  chainId = 109,
  onBack,
  onSuccess,
  refetchPosition
}: RemoveLiquidityViewProps) {
  const [removePercentage, setRemovePercentage] = useState(25);
  const [slippageTolerance, setSlippageTolerance] = useState(1); // 1% default
  const [isTaxToken, setIsTaxToken] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const {
    removeLiquidity,
    state,
    resetState
  } = useLiquidityManagement(chainId);
  const {
    isWrappedNative,
    chainConfig
  } = useNativeTokenHandler(chainId);

  // Detect wrapped native tokens in the position
  const wrappedNativeInfo = useMemo(() => {
    const token0IsWrapped = isWrappedNative(position.token0, position.dex);
    const token1IsWrapped = isWrappedNative(position.token1, position.dex);
    const hasWrappedNative = token0IsWrapped || token1IsWrapped;
    const wrappedToken = token0IsWrapped ? position.token0 : token1IsWrapped ? position.token1 : null;
    const nativeSymbol = chainConfig?.nativeCurrency?.symbol || 'ETH';
    return {
      hasWrappedNative,
      token0IsWrapped,
      token1IsWrapped,
      wrappedToken,
      nativeSymbol
    };
  }, [position, isWrappedNative, chainConfig]);

  // Default to receiving native when wrapped native is detected
  const [receiveNative, setReceiveNative] = useState(wrappedNativeInfo.hasWrappedNative);
  const handleRemove = async () => {
    setShowConfirmModal(false);
    try {
      await removeLiquidity({
        position,
        percentage: removePercentage,
        slippageTolerance,
        deadline: Math.floor(Date.now() / 1000) + 1200,
        // 20 minutes
        isTaxToken,
        receiveNative: wrappedNativeInfo.hasWrappedNative ? receiveNative : false
      });

      // If we have refetchPosition, use targeted refresh
      if (refetchPosition && chainId) {
        console.log('[RemoveLiquidityView] Refetching single position:', position.contractAddress);
        await refetchPosition(position.contractAddress, chainId, position.dex);
      }

      // Wait a moment for state to update
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        onBack();
      }, 1000);
    } catch (error) {
      console.error('Error removing liquidity:', error);
      // Error is already handled in the hook
    }
  };
  const getStatusColor = (position_0: LiquidityPosition) => {
    if (position_0.version === 'v2') return 'primary';
    if (position_0.range?.inRange) return 'success';
    return 'warning';
  };
  const getStatusText = (position_1: LiquidityPosition) => {
    if (position_1.version === 'v2') return 'V2';
    if (position_1.range?.inRange) return 'In Range';
    return 'Out of Range';
  };

  // Calculate amounts to receive
  const token0Amount = parseFloat(position.token0AmountFormatted) * removePercentage / 100;
  const token1Amount = parseFloat(position.token1AmountFormatted) * removePercentage / 100;
  const valueToRemove = position.totalValueUsd * removePercentage / 100;
  const getStepText = () => {
    switch (state.step) {
      case 'approval':
        return 'Approving LP tokens...';
      case 'transaction':
        return 'Removing liquidity...';
      case 'confirmed':
        return 'Liquidity removed!';
      default:
        return 'Remove Liquidity';
    }
  };
  return <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button isIconOnly variant="light" onPress={onBack} className="shrink-0" isDisabled={state.isLoading}>
          <Icon icon="mdi:arrow-left" className="text-xl" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Remove Liquidity</h2>
          <p className="text-default-500">
            Remove liquidity from your {position.token0.symbol}/{position.token1.symbol} position
          </p>
        </div>
      </div>

      {/* Position Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3 w-full">
            <div className="flex items-center -space-x-2">
              <TokenLogo chainId={chainId!} tokenAddress={position.token0.address as Address} size="md" className="border-2 border-background" fallbackText={position.token0.symbol?.charAt(0)} />
              <TokenLogo chainId={chainId!} tokenAddress={position.token1.address as Address} size="md" className="border-2 border-background" fallbackText={position.token1.symbol?.charAt(0)} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  {position.token0.symbol}/{position.token1.symbol}
                </h3>
                <Chip size="sm" color={getStatusColor(position)} variant="flat">
                  {getStatusText(position)}
                </Chip>
              </div>
              <div className="flex items-center gap-2 text-sm text-default-500">
                <img src={DEX_LIST[position.dex].logo} alt={position.dexName} className="w-4 h-4" />
                {position.dexName}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold">
                ${formatNumber(position.totalValueUsd, 2)}
              </div>
              <div className="text-sm text-default-500">Total Value</div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Remove Amount Selector */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Amount to Remove</h3>
        </CardHeader>
        <CardBody className="space-y-6">
          {/* Percentage Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-default-500">Remove Percentage</span>
              <span className="text-lg font-semibold">{removePercentage}%</span>
            </div>
            <Slider size="lg" step={1} minValue={1} maxValue={100} value={removePercentage} onChange={value => setRemovePercentage(Array.isArray(value) ? value[0] : value)} className="w-full" color="primary" isDisabled={state.isLoading} />
            <div className="flex gap-2">
              {[25, 50, 75, 100].map(percent => <Button key={percent} size="sm" variant={removePercentage === percent ? "solid" : "bordered"} color={removePercentage === percent ? "primary" : "default"} onPress={() => setRemovePercentage(percent)} isDisabled={state.isLoading}>
                  {percent}%
                </Button>)}
            </div>
          </div>

          <Divider />

          {/* Settings */}
          <div className="space-y-4">
            <h4 className="font-semibold">Settings</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Slippage Tolerance</span>
                <div className="flex gap-2 items-center">
                  {[0.1, 0.5].map(slippage => <Button key={slippage} size="sm" variant={slippageTolerance === slippage ? "solid" : "bordered"} color={slippageTolerance === slippage ? "primary" : "default"} onPress={() => setSlippageTolerance(slippage)} isDisabled={state.isLoading}>
                      {slippage}%
                    </Button>)}
                  <Input type="number" size="sm" placeholder="Custom" value={slippageTolerance.toString()} onChange={e => {
                  const value_0 = parseFloat(e.target.value);
                  if (!isNaN(value_0) && value_0 >= 0 && value_0 <= 100) {
                    setSlippageTolerance(value_0);
                  }
                }} endContent={<span className="text-xs text-default-400">%</span>} className="w-24" isDisabled={state.isLoading} min={0} max={100} step={0.1} />
                </div>
              </div>
              {slippageTolerance > 5 && <div className="flex items-start gap-2 p-2 bg-warning-50 rounded-lg border border-warning-200">
                  <Icon icon="mdi:alert" className="text-warning text-sm shrink-0 mt-0.5" />
                  <span className="text-xs text-warning-800">
                    High slippage ({slippageTolerance}%) may result in unfavorable rates
                  </span>
                </div>}
              <Checkbox isSelected={isTaxToken} onValueChange={setIsTaxToken} isDisabled={state.isLoading}>
                <div className="flex flex-col">
                  <span className="text-sm">Tax/Fee on Transfer Token</span>
                  <span className="text-xs text-default-400">
                    Enable if either token has a transfer fee (e.g., reflections, burns)
                  </span>
                </div>
              </Checkbox>
            </div>
          </div>

          <Divider />

          {/* Amounts to Receive */}
          <div className="space-y-4">
            <h4 className="font-semibold">You will receive:</h4>
            <div className="space-y-3">
              {/* Token 0 */}
              <div className="flex items-center justify-between p-3 bg-default-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <TokenLogo chainId={chainId!} tokenAddress={position.token0.address as Address} size="sm" fallbackText={position.token0.symbol?.charAt(0)} />
                  {wrappedNativeInfo.token0IsWrapped ? <Dropdown>
                      <DropdownTrigger>
                        <Button size="sm" variant="flat" className="min-w-[100px] justify-between" endContent={<Icon icon="mdi:chevron-down" className="text-sm" />} isDisabled={state.isLoading}>
                          {receiveNative ? wrappedNativeInfo.nativeSymbol : position.token0.symbol}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="Select token format" selectionMode="single" selectedKeys={new Set([receiveNative ? 'native' : 'wrapped'])} onSelectionChange={keys => {
                    const selected = Array.from(keys)[0];
                    setReceiveNative(selected === 'native');
                  }}>
                        <DropdownItem key="native" startContent={<Icon icon="mdi:ethereum" />}>
                          {wrappedNativeInfo.nativeSymbol} (Native)
                        </DropdownItem>
                        <DropdownItem key="wrapped" startContent={<Icon icon="mdi:wrap" />}>
                          {position.token0.symbol} (Wrapped)
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown> : <span className="font-medium">{position.token0.symbol}</span>}
                </div>
                <div className="text-right">
                  <div className="font-semibold">
                    {formatNumber(token0Amount, 6)}
                  </div>
                </div>
              </div>
              {/* Token 1 */}
              <div className="flex items-center justify-between p-3 bg-default-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <TokenLogo chainId={chainId!} tokenAddress={position.token1.address as Address} size="sm" fallbackText={position.token1.symbol?.charAt(0)} />
                  {wrappedNativeInfo.token1IsWrapped ? <Dropdown>
                      <DropdownTrigger>
                        <Button size="sm" variant="flat" className="min-w-[100px] justify-between" endContent={<Icon icon="mdi:chevron-down" className="text-sm" />} isDisabled={state.isLoading}>
                          {receiveNative ? wrappedNativeInfo.nativeSymbol : position.token1.symbol}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="Select token format" selectionMode="single" selectedKeys={new Set([receiveNative ? 'native' : 'wrapped'])} onSelectionChange={keys_0 => {
                    const selected_0 = Array.from(keys_0)[0];
                    setReceiveNative(selected_0 === 'native');
                  }}>
                        <DropdownItem key="native" startContent={<Icon icon="mdi:ethereum" />}>
                          {wrappedNativeInfo.nativeSymbol} (Native)
                        </DropdownItem>
                        <DropdownItem key="wrapped" startContent={<Icon icon="mdi:wrap" />}>
                          {position.token1.symbol} (Wrapped)
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown> : <span className="font-medium">{position.token1.symbol}</span>}
                </div>
                <div className="text-right">
                  <div className="font-semibold">
                    {formatNumber(token1Amount, 6)}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-lg font-semibold">
              <span>Total Value:</span>
              <span>${formatNumber(valueToRemove, 2)}</span>
            </div>
          </div>

          {/* Transaction Status */}
          {state.error && <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon icon="mdi:alert-circle" className="text-danger text-xl shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-danger">Transaction Failed</div>
                  <div className="text-sm text-danger-700 mt-1">{state.error}</div>
                </div>
              </div>
            </div>}

          {state.txHash && state.step === 'confirmed' && <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon icon="mdi:check-circle" className="text-success text-xl shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-success">Transaction Confirmed!</div>
                  <div className="text-sm text-success-700 mt-1">
                    Liquidity removed successfully
                  </div>
                  {state.txHash && <a href={`https://shibariumscan.io/tx/${state.txHash}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1">
                      View on Explorer
                      <Icon icon="mdi:open-in-new" className="text-sm" />
                    </a>}
                </div>
              </div>
            </div>}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button variant="light" onPress={onBack} className="flex-1" isDisabled={state.isLoading}>
              Cancel
            </Button>
            <Button color="danger" onPress={() => setShowConfirmModal(true)} isLoading={state.isLoading} className="flex-1" startContent={!state.isLoading ? <Icon icon="mdi:minus" /> : undefined} isDisabled={state.step === 'confirmed'}>
              {state.isLoading ? getStepText() : 'Remove Liquidity'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} size="md">
        <ModalContent>
          <ModalHeader>Confirm Remove Liquidity</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-default-600">
                You are about to remove {removePercentage}% of your liquidity position.
              </p>
              <div className="bg-default-100 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-default-500">Remove Amount:</span>
                  <span className="font-semibold">{removePercentage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-default-500">Slippage Tolerance:</span>
                  <span className="font-semibold">{slippageTolerance}%</span>
                </div>
                <Divider />
                <div className="flex justify-between">
                  <span className="text-sm text-default-500">
                    {wrappedNativeInfo.token0IsWrapped && receiveNative ? wrappedNativeInfo.nativeSymbol : position.token0.symbol}:
                  </span>
                  <span className="font-semibold">{formatNumber(token0Amount, 6)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-default-500">
                    {wrappedNativeInfo.token1IsWrapped && receiveNative ? wrappedNativeInfo.nativeSymbol : position.token1.symbol}:
                  </span>
                  <span className="font-semibold">{formatNumber(token1Amount, 6)}</span>
                </div>
                <Divider />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold">Total Value:</span>
                  <span className="font-bold">${formatNumber(valueToRemove, 2)}</span>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-warning-50 rounded-lg border border-warning-200">
                <Icon icon="mdi:alert" className="text-warning text-lg shrink-0 mt-0.5" />
                <p className="text-sm text-warning-800">
                  This action cannot be undone. Please make sure you have reviewed all the details before proceeding.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleRemove} startContent={<Icon icon="mdi:check" />}>
              Confirm Remove
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>;
}